import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import './Gallery.css';
// Import images
import image1 from '../../../../assets/images/gallery/img1.jpg';
import image2 from '../../../../assets/images/gallery/img2.jpg';
import image3 from '../../../../assets/images/gallery/img3.jpg';
import image4 from '../../../../assets/images/gallery/img4.jpg';
import image5 from '../../../../assets/images/gallery/img5.jpg';
import image6 from '../../../../assets/images/gallery/img6.jpg';
import video1 from '../../../../assets/video/video1.mp4';

const Gallery = () => {
  // Sample gallery data - in a real app, this would come from props or an API
  const galleryData = [
    { 
      id: 1, 
      type: 'image',
      src: image1, 
      title: 'Holi Festival', 
      description: 'Students & Staff celebrating', 
      category: 'events',
      tags: ['festival', 'celebration', 'colors']
    },
    { 
      id: 2, 
      type: 'image',
      src: image2, 
      title: 'Campus Life', 
      description: 'A day in the campus', 
      category: 'campus',
      tags: ['students', 'architecture', 'daily']
    },
    { 
      id: 3, 
      type: 'video',
      src: video1, 
      videoUrl: video1,
      thumbnailTime: 3.85, // Capture thumbnail at 1.5 seconds into video
      duration: '3:45',
      title: 'Workshop Session', 
      description: 'Technical workshop on web development', 
      category: 'education',
      tags: ['learning', 'technology', 'workshop']
    },
    { 
      id: 4, 
      type: 'image',
      src: image3, 
      title: 'Sports Day', 
      description: 'Annual sports competition', 
      category: 'events',
      tags: ['sports', 'competition', 'athletics']
    },
    { 
      id: 5, 
      type: 'video',
      src: video1, // Using a real video file
      videoUrl: video1,
      thumbnailTime: 3, // Capture thumbnail at 3 seconds into video
      duration: '2:18',
      title: 'Campus Tour', 
      description: 'Virtual tour of our facilities', 
      category: 'campus',
      tags: ['tour', 'facilities', 'architecture']
    },
    { 
      id: 6, 
      type: 'image',
      src: image5, 
      title: 'Graduation', 
      description: 'Graduation ceremony 2024', 
      category: 'education',
      tags: ['graduation', 'ceremony', 'achievement']
    },
  ];

  const [images, setImages] = useState(galleryData);
  const [filteredImages, setFilteredImages] = useState(galleryData);
  const [activeFilter, setActiveFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedItem, setSelectedItem] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [fullscreen, setFullscreen] = useState(false);
  const [previewHoveredId, setPreviewHoveredId] = useState(null);
  const [videoLoaded, setVideoLoaded] = useState({});
  const [thumbnails, setThumbnails] = useState({});
  const [videoMetadata, setVideoMetadata] = useState({});
  const [mediaAspectRatios, setMediaAspectRatios] = useState({});
  const [modalSize, setModalSize] = useState({ width: 0, height: 0 });
  const [videoPlayerSize, setVideoPlayerSize] = useState({ width: 0, height: 0 });
  
  const videoRef = useRef(null);
  const previewRefs = useRef({});
  const thumbnailCanvasRef = useRef(document.createElement('canvas'));
  const gridRef = useRef(null);
  const modalContentRef = useRef(null);
  const modalMediaRef = useRef(null);
  
  // Get unique categories
  const categories = ['all', ...new Set(images.map(item => item.category))];
  
  // Generate thumbnails and collect media metadata on component mount
  useEffect(() => {
    const videoItems = galleryData.filter(item => item.type === 'video');
    const imageItems = galleryData.filter(item => item.type === 'image');
    
    // Process videos to extract thumbnails and dimensions
    const processVideos = async () => {
      for (const item of videoItems) {
        await generateThumbnail(item);
      }
    };
    
    // Process images to get dimensions
    const processImages = async () => {
      for (const item of imageItems) {
        await getImageDimensions(item);
      }
    };
    
    processVideos();
    processImages();
  }, []);
  
  // Function to get image dimensions
  const getImageDimensions = (item) => {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        const aspectRatio = img.width / img.height;
        setMediaAspectRatios(prev => ({...prev, [item.id]: aspectRatio}));
        resolve();
      };
      img.onerror = () => {
        console.error('Error loading image for dimension calculation');
        resolve();
      };
      img.src = item.src;
    });
  };
  
  // Function to generate a thumbnail from a video
  const generateThumbnail = (item) => {
    return new Promise((resolve) => {
      const video = document.createElement('video');
      video.src = item.videoUrl;
      video.crossOrigin = 'anonymous';
      video.muted = true;
      video.preload = 'metadata';
      
      // When video metadata is loaded
      video.onloadedmetadata = () => {
        // Store video metadata and aspect ratio
        const aspectRatio = video.videoWidth / video.videoHeight;
        setVideoMetadata(prev => ({
          ...prev, 
          [item.id]: {
            duration: video.duration,
            width: video.videoWidth,
            height: video.videoHeight
          }
        }));
        setMediaAspectRatios(prev => ({...prev, [item.id]: aspectRatio}));
        
        // Format duration as mm:ss
        const minutes = Math.floor(video.duration / 60);
        const seconds = Math.floor(video.duration % 60);
        const formattedDuration = `${minutes}:${seconds < 10 ? '0' + seconds : seconds}`;
        
        // Update gallery item with actual duration
        setImages(prev => 
          prev.map(img => 
            img.id === item.id ? {...img, duration: formattedDuration} : img
          )
        );
        
        // Set time to specified point or 10% into the video for the thumbnail
        video.currentTime = item.thumbnailTime || (video.duration * 0.1);
      };
      
      // When video is seeked to the desired timestamp
      video.onseeked = () => {
        const canvas = thumbnailCanvasRef.current;
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        
        const ctx = canvas.getContext('2d');
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        
        // Get data URL from canvas
        try {
          const thumbnailUrl = canvas.toDataURL('image/jpeg');
          
          // Store thumbnail
          setThumbnails(prev => ({...prev, [item.id]: thumbnailUrl}));
          
          // Clean up
          video.pause();
          resolve();
        } catch (err) {
          console.error('Error generating thumbnail:', err);
          resolve();
        }
      };
      
      // Handle errors
      video.onerror = () => {
        console.error('Error loading video for thumbnail generation');
        resolve();
      };
      
      // Start loading the video
      video.load();
    });
  };
  
  // Apply consistent height to media items after they're rendered
  useEffect(() => {
    if (!loading && gridRef.current) {
      equalizeMediaHeights();
    }
  }, [filteredImages, loading]);
  
  // Function to equalize media heights
  const equalizeMediaHeights = () => {
    // Get all media containers
    const mediaContainers = document.querySelectorAll('.image-container');
    
    if (mediaContainers.length === 0) return;
    
    // Determine target height (can be fixed or calculated)
    const targetHeight = 220; // Fixed height in pixels
    
    // Apply the height to all containers
    mediaContainers.forEach(container => {
      container.style.height = `${targetHeight}px`;
      
      // Find the media element (img or video)
      const mediaElement = container.querySelector('img') || container.querySelector('video');
      if (mediaElement) {
        // Reset any previous styles
        mediaElement.style.width = '';
        mediaElement.style.height = '';
        mediaElement.style.maxHeight = '100%';
        
        // Get the item ID to find its aspect ratio
        const itemId = parseInt(container.closest('.gallery-item').getAttribute('data-id'));
        const aspectRatio = mediaAspectRatios[itemId];
        
        if (aspectRatio) {
          // Calculate the width needed for this height while preserving aspect ratio
          const calculatedWidth = targetHeight * aspectRatio;
          
          // Ensure the media is centered if it's wider than the container
          mediaElement.style.width = '100%';
          mediaElement.style.height = '100%';
          mediaElement.style.objectFit = 'cover';
          mediaElement.style.objectPosition = 'center';
        }
      }
    });
  };
  
  // Calculate and set optimal video player dimensions
  const calculateVideoPlayerSize = useCallback(() => {
    if (!selectedItem || selectedItem.type !== 'video' || !modalMediaRef.current) {
      return;
    }
    
    const modalMediaRect = modalMediaRef.current.getBoundingClientRect();
    const availableWidth = modalMediaRect.width;
    const availableHeight = modalMediaRect.height;
    
    // Get video aspect ratio from metadata
    const aspectRatio = mediaAspectRatios[selectedItem.id] || 16/9; // Default to 16:9 if not available
    
    let optimalWidth, optimalHeight;
    
    // Calculate dimensions while maintaining aspect ratio
    if (availableWidth / aspectRatio <= availableHeight) {
      // Width is the limiting factor
      optimalWidth = availableWidth;
      optimalHeight = availableWidth / aspectRatio;
    } else {
      // Height is the limiting factor
      optimalHeight = availableHeight;
      optimalWidth = availableHeight * aspectRatio;
    }
    
    // Apply a small margin to ensure it fits nicely
    optimalWidth = Math.floor(optimalWidth * 0.95);
    optimalHeight = Math.floor(optimalHeight * 0.95);
    
    setVideoPlayerSize({
      width: optimalWidth,
      height: optimalHeight
    });
    
    // Apply size to video element
    if (videoRef.current) {
      videoRef.current.style.width = `${optimalWidth}px`;
      videoRef.current.style.height = `${optimalHeight}px`;
    }
  }, [selectedItem, mediaAspectRatios]);
  
  // Recalculate modal and player size when the modal opens or resizes
  useEffect(() => {
    if (selectedItem) {
      // Set a small timeout to ensure the modal has rendered completely
      const timer = setTimeout(() => {
        calculateVideoPlayerSize();
      }, 100);
      
      // Add window resize handler
      window.addEventListener('resize', calculateVideoPlayerSize);
      
      return () => {
        clearTimeout(timer);
        window.removeEventListener('resize', calculateVideoPlayerSize);
      };
    }
  }, [selectedItem, fullscreen, calculateVideoPlayerSize]);
  
  // Filter media items based on selected category and search query
  useEffect(() => {
    setLoading(true);
    setTimeout(() => {
      let filtered = images;
      
      // Apply category filter
      if (activeFilter !== 'all') {
        filtered = filtered.filter(item => item.category === activeFilter);
      }
      
      // Apply search filter if search query exists
      if (searchQuery.trim()) {
        const query = searchQuery.toLowerCase();
        filtered = filtered.filter(item => 
          item.title.toLowerCase().includes(query) || 
          item.description.toLowerCase().includes(query) ||
          item.category.toLowerCase().includes(query) ||
          (item.tags && item.tags.some(tag => tag.toLowerCase().includes(query)))
        );
      }
      
      setFilteredImages(filtered);
      setLoading(false);
    }, 500);
  }, [activeFilter, searchQuery, images]);
  
  // Handle item selection
  const handleItemClick = useCallback((item) => {
    // Stop any preview videos that might be playing
    if (previewHoveredId && previewRefs.current[previewHoveredId]) {
      previewRefs.current[previewHoveredId].pause();
      setPreviewHoveredId(null);
    }
    
    setSelectedItem(item);
    setCurrentIndex(filteredImages.findIndex(i => i.id === item.id));
  }, [filteredImages, previewHoveredId]);
  
  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      equalizeMediaHeights();
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [filteredImages]);
  
  // Navigation through gallery modal
  const navigate = useCallback((direction) => {
    const newIndex = currentIndex + direction;
    
    if (newIndex >= 0 && newIndex < filteredImages.length) {
      // If current item is video, pause it before changing
      if (selectedItem?.type === 'video' && videoRef.current) {
        videoRef.current.pause();
      }
      
      setCurrentIndex(newIndex);
      setSelectedItem(filteredImages[newIndex]);
    }
  }, [currentIndex, filteredImages, selectedItem]);
  
  // Handle search input
  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
  };
  
  // Reset search and filters
  const resetFilters = () => {
    setSearchQuery('');
    setActiveFilter('all');
  };
  
  // Close modal and reset video if needed
  const closeModal = () => {
    if (selectedItem?.type === 'video' && videoRef.current) {
      videoRef.current.pause();
    }
    setSelectedItem(null);
    setFullscreen(false);
  };
  
  // Toggle fullscreen mode
  const toggleFullscreen = () => {
    const newFullscreenState = !fullscreen;
    setFullscreen(newFullscreenState);
    
    // Wait for the DOM to update before recalculating size
    setTimeout(() => {
      calculateVideoPlayerSize();
    }, 100);
  };
  
  // Generate thumbnail frames for a specific video
  const generateVideoFrames = async (item, numFrames = 3) => {
    const frames = [];
    const video = document.createElement('video');
    video.src = item.videoUrl;
    video.crossOrigin = 'anonymous';
    video.muted = true;
    
    await new Promise(resolve => {
      video.onloadedmetadata = resolve;
      video.load();
    });
    
    const duration = video.duration;
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');
    
    // Generate frames at different timestamps
    for (let i = 0; i < numFrames; i++) {
      const timestamp = (duration * (i + 1)) / (numFrames + 1);
      
      await new Promise(resolve => {
        video.currentTime = timestamp;
        video.onseeked = resolve;
      });
      
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      frames.push(canvas.toDataURL('image/jpeg'));
    }
    
    video.pause();
    return frames;
  };
  
  // Video preview hover handlers
  const handleVideoHover = (id) => {
    // Only start preview if we have a ref and it's different from current preview
    if (previewRefs.current[id] && id !== previewHoveredId) {
      // Stop any current preview
      if (previewHoveredId && previewRefs.current[previewHoveredId]) {
        previewRefs.current[previewHoveredId].pause();
      }
      
      const videoElement = previewRefs.current[id];
      
      // Set the preview video to muted, low volume and play
      videoElement.muted = true;
      videoElement.volume = 0;
      videoElement.currentTime = 0;
      
      // Play with a slight delay to avoid unnecessary previews during quick scrolling
      const playTimer = setTimeout(() => {
        const playPromise = videoElement.play();
        
        // Handle play promise to avoid errors
        if (playPromise !== undefined) {
          playPromise.catch(error => {
            console.log('Auto-play prevented:', error);
          });
        }
      }, 300);
      
      setPreviewHoveredId(id);
      
      return () => clearTimeout(playTimer);
    }
  };
  
  const handleVideoLeave = (id) => {
    if (previewRefs.current[id]) {
      previewRefs.current[id].pause();
      if (id === previewHoveredId) {
        setPreviewHoveredId(null);
      }
    }
  };
  
  // Handle video load state
  const handleVideoLoad = (id) => {
    setVideoLoaded(prev => ({...prev, [id]: true}));
    
    // If this is for the currently selected video in modal, calculate its optimal size
    if (selectedItem && selectedItem.id === id && selectedItem.type === 'video') {
      calculateVideoPlayerSize();
    }
  };
  
  // Format seconds to MM:SS
  const formatDuration = (seconds) => {
    if (!seconds || isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs < 10 ? '0' + secs : secs}`;
  };
  
  // Generate several screenshots at different timestamps
  const captureFrames = async (videoItem, timestamps = [0.25, 0.5, 0.75]) => {
    const frames = [];
    const video = document.createElement('video');
    video.src = videoItem.videoUrl;
    video.muted = true;
    
    return new Promise((resolve) => {
      video.onloadeddata = async () => {
        for (const time of timestamps) {
          video.currentTime = video.duration * time;
          await new Promise(r => setTimeout(r, 100));
          
          const canvas = document.createElement('canvas');
          canvas.width = video.videoWidth;
          canvas.height = video.videoHeight;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
          frames.push(canvas.toDataURL());
        }
        video.pause();
        resolve(frames);
      };
      video.load();
    });
  };
  
  // Extract current time from video for screenshot
  const captureCurrentFrame = () => {
    if (videoRef.current && selectedItem?.type === 'video') {
      const canvas = document.createElement('canvas');
      canvas.width = videoRef.current.videoWidth;
      canvas.height = videoRef.current.videoHeight;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(videoRef.current, 0, 0, canvas.width, canvas.height);
      
      // You can save this image, download it, or store it
      const screenshotUrl = canvas.toDataURL('image/jpeg');
      
      // Example: Create a download link
      const a = document.createElement('a');
      a.href = screenshotUrl;
      a.download = `${selectedItem.title}-screenshot.jpg`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    }
  };
  
  // Keyboard navigation
  useEffect(() => {
    const handleKeyPress = (e) => {
      if (!selectedItem) return;
      
      switch(e.key) {
        case 'ArrowLeft': navigate(-1); break;
        case 'ArrowRight': navigate(1); break;
        case 'Escape': closeModal(); break;
        case 'f': toggleFullscreen(); break;
        case 's': if (selectedItem?.type === 'video') captureCurrentFrame(); break;
        default: break;
      }
    };
    
    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [navigate, selectedItem, fullscreen]);
  
  // Animation variants
  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: i => ({
      opacity: 1,
      y: 0,
      transition: { delay: i * 0.1, duration: 0.4 }
    })
  };

  return (
    <div className="gallery-container">
      <div className="gallery-header">
        <h2 className="gallery-title">Media Gallery</h2>
        <p className="gallery-subtitle">Explore our collection of photos and videos</p>
        
        {/* Search and Filter Controls */}
        <div className="gallery-controls">
          <div className="search-container">
            <input
              type="text"
              placeholder="Search by title, description or tags..."
              value={searchQuery}
              onChange={handleSearch}
              className="search-input"
            />
            {searchQuery && (
              <button className="search-clear" onClick={() => setSearchQuery('')}>
                ✕
              </button>
            )}
          </div>
          
          <div className="filter-container">
            {categories.map(category => (
              <button
                key={category}
                className={`filter-btn ${activeFilter === category ? 'active' : ''}`}
                onClick={() => setActiveFilter(category)}
              >
                {category.charAt(0).toUpperCase() + category.slice(1)}
              </button>
            ))}
          </div>
        </div>
        
        {/* Results summary and reset button */}
        <div className="results-info">
          <span>{filteredImages.length} items found</span>
          {(activeFilter !== 'all' || searchQuery) && (
            <button className="reset-btn" onClick={resetFilters}>
              Reset Filters
            </button>
          )}
        </div>
      </div>
      
      {loading ? (
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading gallery...</p>
        </div>
      ) : filteredImages.length === 0 ? (
        <div className="no-results">
          <div className="no-results-icon">🔍</div>
          <h3>No matching items found</h3>
          <p>Try adjusting your search or filters</p>
          <button className="reset-btn" onClick={resetFilters}>
            Clear All Filters
          </button>
        </div>
      ) : (
        <div className="gallery-grid" ref={gridRef}>
          {filteredImages.map((item, index) => (
            <motion.div
              className="gallery-item"
              key={item.id}
              data-id={item.id}
              custom={index}
              initial="hidden"
              animate="visible"
              variants={itemVariants}
              whileHover={{ y: -8, transition: { duration: 0.2 } }}
              onClick={() => handleItemClick(item)}
              onMouseEnter={() => item.type === 'video' && handleVideoHover(item.id)}
              onMouseLeave={() => item.type === 'video' && handleVideoLeave(item.id)}
            >
              <div className="image-card">
                <div className={`image-container ${item.type === 'video' ? 'video-container' : ''}`}>
                  {item.type === 'video' ? (
                    <>
                      {/* Video Preview with Generated Thumbnail Fallback */}
                      <div className="video-preview-wrapper">
                        <img 
                          src={thumbnails[item.id] || item.src} 
                          alt={item.title} 
                          className={`video-thumbnail ${previewHoveredId === item.id ? 'hidden' : ''}`}
                        />
                        <video
                          ref={el => previewRefs.current[item.id] = el}
                          src={item.videoUrl}
                          className={`video-preview ${previewHoveredId === item.id ? 'active' : ''}`}
                          preload="metadata"
                          muted
                          playsInline
                          onLoadedData={() => handleVideoLoad(item.id)}
                        />
                      </div>
                      <div className="video-duration">{item.duration || '0:00'}</div>
                      <div className="video-play-indicator">
                        <span className="play-icon">▶</span>
                      </div>
                    </>
                  ) : (
                    <img src={item.src} alt={item.title} />
                  )}
                  <div className="overlay">
                    <span className="media-badge">{item.type === 'video' ? '▶ Video' : '🖼 Image'}</span>
                    <span className="view-icon">&#128065;</span>
                  </div>
                </div>
                <div className="image-info">
                  <h3>{item.title}</h3>
                  <p>{item.description}</p>
                  <div className="item-footer">
                    <span className="category-tag">{item.category}</span>
                    {item.type === 'video' && <span className="video-indicator">▶</span>}
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}
      
      {/* Media Viewer Modal */}
      <AnimatePresence>
        {selectedItem && (
          <motion.div 
            className={`modal-overlay ${fullscreen ? 'fullscreen' : ''}`}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closeModal}
          >
            <motion.div 
              className="modal-content"
              ref={modalContentRef}
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              onClick={e => e.stopPropagation()}
            >
              <div className="modal-header">
                <h3>{selectedItem.title}</h3>
                <div className="modal-actions">
                  {selectedItem.type === 'video' && (
                    <button 
                      className="action-btn screenshot"
                      title="Take screenshot"
                      onClick={captureCurrentFrame}
                    >
                      📷
                    </button>
                  )}
                  <button 
                    className="action-btn" 
                    onClick={toggleFullscreen}
                  >
                    {fullscreen ? '⤿' : '⤢'}
                  </button>
                  <button 
                    className="action-btn close" 
                    onClick={closeModal}
                  >
                    ✕
                  </button>
                </div>
              </div>
              
              <div className="modal-body">
                <button 
                  className="nav-btn prev"
                  onClick={e => { e.stopPropagation(); navigate(-1); }}
                  disabled={currentIndex === 0}
                >
                  ←
                </button>
                
                <div className="modal-media" ref={modalMediaRef}>
                  {selectedItem.type === 'video' ? (
                    <div className="video-player-container">
                      <div className="video-loading" style={{ display: videoLoaded[selectedItem.id] ? 'none' : 'flex' }}>
                        <div className="loading-spinner"></div>
                        <span>Loading video...</span>
                      </div>
                      <video 
                        ref={videoRef}
                        src={selectedItem.videoUrl} 
                        poster={thumbnails[selectedItem.id] || selectedItem.src}
                        controls 
                        autoPlay
                        className="modal-video"
                        style={{
                          width: videoPlayerSize.width ? `${videoPlayerSize.width}px` : '100%',
                          height: videoPlayerSize.height ? `${videoPlayerSize.height}px` : 'auto',
                        //   maxWidth: '100%',
                          maxHeight: fullscreen ? '80vh' : '50vh'
                        }}
                        onLoadedData={() => handleVideoLoad(selectedItem.id)}
                      />
                  
                    </div>
                  ) : (
                    <img 
                      src={selectedItem.src} 
                      alt={selectedItem.title} 
                      style={{
                        maxWidth: '100%',
                        maxHeight: fullscreen ? '80vh' : '60vh',
                        objectFit: 'contain'
                      }}
                    />
                  )}
                </div>
                
                <button 
                  className="nav-btn next"
                  onClick={e => { e.stopPropagation(); navigate(1); }}
                  disabled={currentIndex === filteredImages.length - 1}
                >
                  →
                </button>
              </div>
              
        
              
              <div className="modal-footer">
                <div className="image-details">
                  <h4>{selectedItem.title}</h4>
                  <p>{selectedItem.description}</p>
                  {selectedItem.tags && (
                    <div className="tags-container">
                      {selectedItem.tags.map(tag => (
                        <span key={tag} className="tag">{tag}</span>
                      ))}
                    </div>
                  )}
                </div>
                <div className="image-meta">
                  <span className="meta-item">
                    <span className="meta-icon">{selectedItem.type === 'video' ? '🎬' : '🖼️'}</span> 
                    {selectedItem.type === 'video' ? 'Video' : 'Image'}
                  </span>
                  <span className="meta-item">
                    <span className="meta-icon">🏷️</span> {selectedItem.category}
                  </span>
                  <span className="meta-item">
                    <span className="meta-icon">📊</span> {currentIndex + 1} / {filteredImages.length}
                  </span>
                  {selectedItem.type === 'video' && (
                    <span className="meta-item">
                      <span className="meta-icon">⏱️</span> {selectedItem.duration || formatDuration(videoMetadata[selectedItem.id]?.duration)}
                    </span>
                  )}
                  {selectedItem.type === 'video' && videoMetadata[selectedItem.id] && (
                    <span className="meta-item">
                      <span className="meta-icon">📏</span> 
                      {videoMetadata[selectedItem.id].width}×{videoMetadata[selectedItem.id].height}
                    </span>
                  )}
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Gallery;